/**
 * File Web API helper functions.
 * https://developer.mozilla.org/en-US/docs/Web/API/File
 */
import _ from "lodash";
import { transformInternalUrl } from "core/utils/str";
import { MAX_FILE_SIZE } from "constants/attachments";

const dataUriRegex = new RegExp(
  /^(data:)([\w/+-]*)(;charset=[\w-]+|;base64)?,(.*)/gi
);

export const getUploadFormData = file => {
  // do not allow big files:
  if (
    (file.size && file.size > MAX_FILE_SIZE) ||
    (file.fileData && file.fileData.length > MAX_FILE_SIZE)
  ) {
    throw new TypeError(
      `Invalid argument: file. The size of the blob exceeds the hard limit of ${MAX_FILE_SIZE}.`
    );
  }
  // this will instruct the browser to upload the file in chunks
  const formData = new FormData();
  const fileFieldName = "file";

  if (!(file instanceof File) && !(file instanceof Blob)) {
    const blob = new Blob(file.fileData, { type: file.fileType });
    formData.append(fileFieldName, blob);
  } else {
    formData.append(fileFieldName, file);
  }
  return formData;
};

/**
 * Uploads a file in a simple way.
 * @param url
 * @param file {Blob|File|{fileData, fileType}}
 * @returns {Promise<string>}
 */
export async function uploadFile(url, file, getTokenCallback) {
  const access_token = await getTokenCallback();
  const formData = getUploadFormData(file);
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${access_token}`
    },
    body: formData
  });

  if (response.status > 200 && response.status < 400) {
    const headers = response.headers;
    return headers.get("Location");
  } else {
    throw new Error("Failed to upload files.");
  }
}

/**
 * Delete a file from a url
 * @param url
 * @returns {Promise<bool>}
 */
export async function deleteFile(url, getTokenCallback) {
  const access_token = await getTokenCallback();

  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${access_token}`
    }
  });

  if (response.status > 200 && response.status < 400) {
    return Promise.resolve(true);
  } else {
    throw new Error("Failed to upload files.");
  }
}

/**
 * Initiates an authenticated HTTP request with the fetch API by adding the credentials in the http headers.
 * @param url
 * @param signal
 * @return {Promise<Response<unknown, unknown>>}
 */
async function doAuthenticatedGetFetch(
  url,
  signal = undefined,
  getTokenCallback
) {
  const access_token = await getTokenCallback();

  return await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${access_token}`
    },
    signal
  });
}

/**
 * Initiates a file download and prompts the user to select the save destination.
 * Note that this is *not* streaming the file download. This may be a problem if
 * we need to download large files. We have removed the service worker based streaming
 * for the time being because it did not work after upgrading to Webpack 5, and needs
 * some care to be reintroduced.
 * @param url The URL of the file to download
 * @return {Promise} Void promise which resolves when the download is complete
 */
export async function downloadFile(
  url,
  getTokenCallback,
  defaultFilename = "download"
) {
  const changedUrl = await transformInternalUrl(url);
  await doAuthenticatedGetFetch(changedUrl, undefined, getTokenCallback)
    .then(async response => {
      return {
        blob: await response.blob(),
        filename: getFileNameFromResponse(response)
      };
    })
    .then(
      ({ blob, filename }) => {
        const objectUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        document.body.appendChild(anchor);
        anchor.href = objectUrl;
        anchor.download = filename || defaultFilename;
        anchor.click();
        window.URL.revokeObjectURL(objectUrl);
      },
      _err => {
        console.warn("Failed to download?!");
      }
    );
}

async function responseToBlobAndDataUri(response) {
  const bodyText = await response.text();
  return {
    blob: dataUriToBlob(bodyText.replace('"', "")),
    dataUri: bodyText.replace('"', "")
  };
}

function getFileNameFromResponse(response) {
  // this doesn't work in some cases because of a limitation:
  // https://stackoverflow.com/questions/43344819/reading-response-headers-with-fetch-api#44816592
  // this requires also a CORS header from the backend
  const contentDisposition = response.headers.get("Content-Disposition");
  let extractedFileName = null;
  if (contentDisposition) {
    const needle = "filename=";
    extractedFileName = contentDisposition
      .substring(contentDisposition.indexOf(needle) + needle.length)
      .replace('"', "")
      .replace(";", "");
  }

  return extractedFileName?.replace('"', "");
}

/**
 * download a file in a simple way.
 * @param url
 * @param options
 * @returns {Promise<object>}
 */
export async function downloadFileToDataURI(url, options, getTokenCallback) {
  const file = {
    mimeString: null,
    dataUri: null,
    name: "",
    blob: null,
    reader: null
  };
  options = _.defaults(options, {
    dataUriRequired: true
  });

  const changedUrl = await transformInternalUrl(url);
  // todo: wrapper function for authenticated fetch
  // todo: seperate function for chunked download
  // fixme: chunked response would be better
  /**
   * @type Response
   */
  const response = await doAuthenticatedGetFetch(
    changedUrl,
    undefined,
    getTokenCallback
  );

  if (response.status === 200) {
    const fileObject = { blob: null, dataUri: null };
    let isUnknownPayload = false;

    // plot twist: there are cases where scalars-api responds with Content-Type */* and the response body contains
    // a data uri.
    const contentType = response.headers.get("Content-Type");
    if (!contentType) {
      return null;
    }

    // we'd like to handle those cases where the content type is unknown.
    // in those cases we figure out the content type from the data uri,
    // because we know for sure that in that case we will get a data uri.
    if (contentType === "*/*") {
      const { blob, dataUri } = await responseToBlobAndDataUri(response);
      fileObject.blob = blob;
      fileObject.dataUri = dataUri;
      isUnknownPayload = true;
    } else {
      fileObject.blob = await response.blob();
    }

    if (fileObject.blob) {
      const extractedFileName =
        getFileNameFromResponse(response) ?? _.last(url.split("/"));

      if (!extractedFileName) {
        throw new Error(
          "Failed to download the file from server. There was a problem finding the filename."
        );
      } else {
        if (isUnknownPayload) {
          const dataUriParts = fileObject.dataUri.split(",");
          file.mimeString = dataUriParts[0].split(":")[1].split(";")[0];
          if (options.dataUriRequired) {
            file.dataUri = fileObject.dataUri;
          }
        } else if (options.dataUriRequired) {
          file.dataUri = await blobToDataUrl(fileObject.blob);
        }
        file.name = extractedFileName;
        file.blob = fileObject.blob;
        return file;
      }
    } else {
      throw new Error(
        "Failed to download the file from the server. Client side error."
      );
    }
  }
}

/**
 * Converts a blob to a data uri.
 * @param blob {Blob}
 * @returns {Promise<string>}
 */
export async function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const a = new FileReader();
    a.onload = e => {
      resolve(e.target.result);
    };
    a.onerror = e => {
      reject(e);
    };
    a.readAsDataURL(blob);
  });
}

export function validateDataUri(du) {
  return du.match(dataUriRegex);
}

/**
 * Converts a data uri to a blob
 * @param dataUri {string}
 * @returns {null|Blob}
 */
export function dataUriToBlob(dataUri) {
  if (!validateDataUri(dataUri)) {
    return null;
  }

  try {
    const dataUriParts = dataUri.split(",");
    const byteString = atob(dataUriParts[1]);
    const mimeString = dataUriParts[0].split(":")[1].split(";")[0];

    const buffer = new ArrayBuffer(byteString.length);
    const byteArray = new Uint8Array(buffer);

    for (let i = 0; i < byteString.length; i++) {
      byteArray[i] = byteString.charCodeAt(i);
    }

    return new Blob([buffer], { type: mimeString });
  } catch (e) {
    return null;
  }
}
